<!-- The step row, under whichever title this mount has: the dialog's title bar or the page header. -->
<ng-template #stepHeader>
  <ol class="creator-steps" aria-label="Steps">
    @for (step of $stepHeader(); track step.id) {
      <li class="creator-step" [attr.data-state]="step.state" [attr.aria-current]="step.state === 'current' ? 'step' : null">
        <span class="creator-step-marker">
          @if (step.state === 'done') {
            <i class="fa-solid fa-check" aria-hidden="true"></i>
          } @else {
            {{ step.number }}
          }
        </span>
        <span class="creator-step-text">
          <span class="creator-step-caption">{{ step.state === 'skipped' ? 'Not needed' : 'Step ' + step.number }}</span>
          <span class="creator-step-label">{{ step.label }}</span>
        </span>
      </li>
    }
  </ol>
</ng-template>

@if ($showDialogHeader()) {
  <!-- The entity create dialog lends its title bar here so the step row sits directly under the title. -->
  <header class="creator-dialog-header" [attr.data-stepped]="$stepHeader().length > 0">
    <h2 class="creator-dialog-title">{{ $dialogTitle() }}</h2>
    <button type="button" class="creator-dialog-close" aria-label="Close" [disabled]="$busy()" (click)="closeDialog()">
      <i class="fa-regular fa-xmark" aria-hidden="true"></i>
    </button>
  </header>
  @if ($stepHeader().length > 0) {
    <ng-container [ngTemplateOutlet]="stepHeader" />
  }
}

<w-page [mode]="PageMode.Page" [headerSize]="PageHeaderSize.Small" [title]="$title()" [forceShowClose]="true" [closeDisabled]="$busy()" class="creator-frame" (close)="cancel()">
  @if ($stepHeader().length > 0 && !$showDialogHeader()) {
    <ng-container *w-page-body-header>
      <ng-container [ngTemplateOutlet]="stepHeader" />
    </ng-container>
  }

  @if ($actionCommands().length > 0) {
    <ng-container *w-page-header-actions>
      <wb-action-bar [commands]="$actionCommands()" />
    </ng-container>
  }

  <ng-container *w-page-body-content>
    <div class="creator-content">
      @if ($identitySections().length > 0) {
        <aside class="creator-identity" aria-label="Record identity">
          @for (item of $identitySections(); track item.section.path) {
            @if ($sectionBorder()) {
              <wb-panel variant="flat" [title]="item.$title()">
                <wb-section [path]="item.section.path" [columns]="item.section.columns" [collapsible]="item.section.collapsible" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [related]="item.section.related" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
              </wb-panel>
            } @else {
              <wb-section [path]="item.section.path" [title]="item.$title()" [columns]="item.section.columns" [collapsible]="item.section.collapsible" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [related]="item.section.related" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
            }
          }
        </aside>
      }

      <main class="creator-body">
        <div class="creator-body-canvas" [style.max-width]="$contentMaxWidth()">
          @if ($errors().length > 0) {
            <div class="creator-errors" role="alert">
              @for (error of $errors(); track $index) {
                <p>{{ error.message }}</p>
              }
            </div>
          }

          @for (band of $bodyLayout(); track $index) {
            @if (band.type === 'columns') {
              <div class="section-columns" [style.grid-template-columns]="'repeat(' + band.columns.length + ', minmax(0, 1fr))'">
                @for (column of band.columns; track $index) {
                  <div class="section-column">
                    @for (item of column; track item.section.path) {
                      @if ($sectionBorder() && !item.section.related) {
                        <wb-panel variant="section" [title]="item.$title()" [collapsible]="item.section.collapsible">
                          <wb-section [path]="item.section.path" [columns]="item.section.columns" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                        </wb-panel>
                      } @else {
                        <wb-section [path]="item.section.path" [title]="item.$title()" [columns]="item.section.columns" [collapsible]="item.section.collapsible" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [related]="item.section.related" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                      }
                    }
                  </div>
                }
              </div>
            } @else {
              @if ($sectionBorder() && !band.section.section.related) {
                <wb-panel variant="section" [title]="band.section.$title()" [collapsible]="band.section.section.collapsible">
                  <wb-section [path]="band.section.section.path" [columns]="band.section.section.columns" [readonly]="$page().section(band.section.section.path).$readonly()" [fields]="band.section.fields" [fieldGroups]="band.section.fieldGroups" [repeats]="band.section.repeats" [resolver]="band.section.resolver" [customRenderer]="band.section.customRenderer" [customRendererInputs]="band.section.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                </wb-panel>
              } @else {
                <wb-section [path]="band.section.section.path" [title]="band.section.$title()" [columns]="band.section.section.columns" [collapsible]="band.section.section.collapsible" [readonly]="$page().section(band.section.section.path).$readonly()" [fields]="band.section.fields" [fieldGroups]="band.section.fieldGroups" [related]="band.section.section.related" [repeats]="band.section.repeats" [resolver]="band.section.resolver" [customRenderer]="band.section.customRenderer" [customRendererInputs]="band.section.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
              }
            }
          }
        </div>
      </main>
    </div>
  </ng-container>

  @if ($stepCaption()) {
    <ng-container *w-page-footer-left-actions>
      <span class="creator-step-footer" role="status">{{ $stepCaption() }}</span>
    </ng-container>
  }

  <ng-container *w-page-footer-right-actions>
    @if ($navigation().previousId) {
      <wb-button label="Back" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Secondary" [disabled]="$busy()" (onClick)="back()" />
    } @else {
      <wb-button label="Cancel" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Secondary" [disabled]="$busy()" (onClick)="cancel()" />
    }
    @if ($showNext()) {
      <wb-button label="Next" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Primary" [disabled]="$busy()" (onClick)="next()" />
    } @else {
      <wb-button [label]="$submitLabel()" [size]="ButtonSize.Medium" [displayStyle]="ButtonDisplayStyle.Primary" [disabled]="$createDisabled()" [loading]="$submitting() || $page().$saving()" (onClick)="create()" />
    }
  </ng-container>
</w-page>
